(ns metabase.lib.schema.metadata
  (:refer-clojure :exclude [empty? get-in])
  (:require
   #?@(:clj
       ([metabase.util.regex :as u.regex]))
   [clojure.set :as set]
   [medley.core :as m]
   [metabase.lib.schema.binning :as lib.schema.binning]
   [metabase.lib.schema.common :as lib.schema.common]
   [metabase.lib.schema.id :as lib.schema.id]
   [metabase.lib.schema.join :as lib.schema.join]
   [metabase.lib.schema.literal :as lib.schema.literal]
   [metabase.lib.schema.metadata.fingerprint :as lib.schema.metadata.fingerprint]
   [metabase.lib.schema.template-tag :as lib.schema.template-tag]
   [metabase.lib.schema.temporal-bucketing :as lib.schema.temporal-bucketing]
   [metabase.util.malli.registry :as mr]
   [metabase.util.performance :refer [empty? get-in]]))

;;; Column vs Field?
;;;
;;; Lately I've been using `Field` to only mean a something that lives in the application database, i.e. something
;;; that is associated with row in the `Field` table and has an `:id`. I'm using `Column` as a more generic term that
;;; includes not only `Field`s but also the columns returned by a stage of a query, e.g. `SELECT count(*) AS count`
;;; returns a `Column` called `count`, but it's not a `Field` because it's not associated with an actual Field in the
;;; application database.
;;;
;;; Column = any column returned by a query or stage of a query
;;; Field  = a Column that is associated with a capital-F Field in the application database, i.e. has an `:id`
;;;
;;; All Fields are Columns, but not all Columns are Fields.
;;;
;;; Also worth a mention: we also have `Dimension`s, associated with the `dimension` table in the application
;;; database, which can act like psuedo-Fields or affect how we treat normal Fields. For example, Dimensions are used
;;; to implement column remapping, e.g. the GUI might display values of `categories.name` when it presents filter
;;; options for `venues.category_id` -- you can remap a meaningless integer FK column to something more helpful.
;;; 'Human readable values' like these can also be entered manually from the GUI, for example for enum columns. How
;;; will this affect what Lib needs to know or does? Not clear at this point, but we'll probably want to abstract
;;; away dealing with Dimensions in the future so the FE QB GUI doesn't need to special case them.

(mr/def ::column.source
  "`:lib/source` -- where a column came from with respect to the current stage.

  Traditionally, `:lib/source` meant something slightly different -- it denoted what part of the current stage a
  column came from, and thus included two additional options -- `:source/fields`, for columns used by `:fields`, and
  `:source/breakouts`, for columns used in `:breakout`. This was not really useful information and made `:lib/source`
  itself useless for determining if a column was 'inherited' or not (i.e., whether it came from a previous stage,
  source card, or a join, and should get field name refs instead of field ID refs --
  see [[metabase.lib.field.util/inherited-column?]])."
  [:enum
   {:decode/normalize (fn [k]
                        (when-let [k (lib.schema.common/normalize-keyword k)]
                          ;; TODO (Cam 7/1/25) -- if we wanted, we could use `:source/breakouts` to populate
                          ;; `:lib/breakout?` -- but I think that isn't really necessary since we can
                          ;; recalculate that information anyway.
                          (when-not (#{:source/fields :source/breakouts} k)
                            k)))}
   ;; these are for things from some sort of source other than the current stage;
   ;; they must be referenced with string names rather than Field IDs
   :source/card
   :source/native
   :source/previous-stage
   ;; these are for things that were introduced by the current stage of the query; `:field` references should be
   ;; referenced with Field IDs if available.
   ;;
   ;; default columns returned by the `:source-table` for the current stage.
   :source/table-defaults
   ;; Introduced by `:aggregation`(s) IN THE CURRENT STAGE. Aggregations by definition are always returned.
   :source/aggregations
   ;; introduced by a join, not necessarily ultimately returned.
   :source/joins
   ;; Introduced by `:expressions` IN THE CURRENT STAGE; not necessarily ultimately returned.
   :source/expressions
   ;; Not even introduced, but 'visible' because this column is implicitly joinable.
   :source/implicitly-joinable
   ;; The synthetic `pivot-grouping` column emitted by the SQL compiler from `GROUPING(...)` when the query carries a
   ;; top-level `:pivot` clause. Treated like an aggregation/native column for alias-info purposes.
   :source/pivot-grouping])

;;; The way FieldValues/remapping works is hella confusing, because it involves the FieldValues table and Dimension
;;; table, and the `has_field_values` column, nobody knows why life is like this TBH. The docstrings
;;; in [[metabase.warehouse-schema.models.field-values]], [[metabase.parameters.chain-filter]],
;;; and [[metabase.query-processor.middleware.add-remaps]] explain this stuff in more detail, read
;;; those and then maybe you will understand what the hell is going on.

(def column-has-field-values-options
  "Possible options for column metadata `:has-field-values`. This is used to determine whether we keep FieldValues for a
  Field (during sync), and which type of widget should be used to pick values of this Field when filtering by it in
  the Query Builder. Not otherwise used by Lib (except for [[metabase.lib.field/field-values-search-info]], which is
  a frontend convenience) or QP at the time of this writing. For column remapping purposes in the Query Processor and
  Lib we just ignore `has_field_values` and only look for FieldValues/Dimension."
  ;; AUTOMATICALLY-SET VALUES, SET DURING SYNC
  ;;
  ;; `nil` -- means infer which widget to use based on logic in [[metabase.lib.field/infer-has-field-values]]; this
  ;; will either return `:search` or `:none`.
  ;;
  ;; This is the default state for Fields not marked `auto-list`. Admins cannot explicitly mark a Field as
  ;; `has_field_values` `nil`. This value is also subject to automatically change in the future if the values of a
  ;; Field change in such a way that it can now be marked `auto-list`. Fields marked `nil` do *not* have FieldValues
  ;; objects.
  ;;
  #{;; The other automatically-set option. Automatically marked as a 'List' Field based on cardinality and other factors
    ;; during sync. Store a FieldValues object; use the List Widget. If this Field goes over the distinct value
    ;; threshold in a future sync, the Field will get switched back to `has_field_values = nil`.
    ;;
    ;; Note that when this comes back from the REST API or [[metabase.lib.field/field-values-search-info]] we always
    ;; return this as `:list` instead of `:auto-list`; this is done by [[metabase.lib.field/infer-has-field-values]].
    ;; I guess this is because the FE isn't supposed to need to care about whether this is `:auto-list` vs `:list`;
    ;; those distinctions are only important for sync I guess.
    :auto-list
    ;;
    ;; EXPLICITLY-SET VALUES, SET BY AN ADMIN
    ;;
    ;; Admin explicitly marked this as a 'Search' Field, which means we should *not* keep FieldValues, and should use
    ;; Search Widget.
    :search
    ;; Admin explicitly marked this as a 'List' Field, which means we should keep FieldValues, and use the List
    ;; Widget. Unlike `auto-list`, if this Field grows past the normal cardinality constraints in the future, it will
    ;; remain `List` until explicitly marked otherwise.
    :list
    ;; Admin explicitly marked that this Field shall always have a plain-text widget, neither allowing search, nor
    ;; showing a list of possible values. FieldValues not kept.
    :none})

(mr/def ::column.has-field-values
  (into [:enum {:decode/normalize lib.schema.common/normalize-keyword}] (sort column-has-field-values-options)))

(mr/def ::column.remapping.external
  "External remapping (Dimension) for a column. From the [[metabase.warehouse-schema.models.dimension]] with `type =
  external` associated with a `Field` in the application database.
  See [[metabase.query-processor.middleware.add-remaps]] for what this means."
  [:map {:closed true}
   [:lib/type [:= {:decode/normalize lib.schema.common/normalize-keyword} :metadata.column.remapping/external]]
   [:id       ::lib.schema.id/dimension]
   ;; from `dimension.name`
   [:name     ::lib.schema.common/non-blank-string]
   ;; `dimension.human_readable_field_id` in the application database. ID of the Field to get human-readable values
   ;; from. e.g. if the column in question is `venues.category-id`, then this would be the ID of `categories.name`
   [:field-id ::lib.schema.id/field]])

(mr/def ::column.remapping.internal
  "Internal remapping (FieldValues) for a column. From [[metabase.warehouse-schema.models.dimension]] with `type =
  internal` and the [[metabase.warehouse-schema.models.field-values]] associated with a `Field` in the application
  database. See [[metabase.query-processor.middleware.add-remaps]] for what this means."
  [:map {:closed true}
   [:lib/type              [:= {:decode/normalize lib.schema.common/normalize-keyword} :metadata.column.remapping/internal]]
   [:id                    ::lib.schema.id/dimension]
   ;; from `dimension.name`
   [:name                  ::lib.schema.common/non-blank-string]
   ;; From `metabase_fieldvalues.values`. Original values
   [:values                [:sequential [:ref ::lib.schema.literal/literal]]]
   ;; From `metabase_fieldvalues.human_readable_values`. Human readable remaps for the values at the same indexes in
   ;; `:values`
   [:human-readable-values [:sequential [:ref ::lib.schema.literal/literal]]]])

;; these can both be empty strings like `""` because SQL Server (and possibly some other DBs) allow empty strings as
;; column identifiers

(mr/def ::source-column-alias
  "Name for a column as returned/projected by the previous stage of the query or source Table/source Card. The
  left-hand side (LHS) of

    SELECT lhs AS rhs"
  :string)

(mr/def ::desired-column-alias
  "Name we should use as a column alias for a column in this stage of a query. The desired column alias in stage N
  becomes the source column alias in stage N+1. The right-hand side (RHS) in

    SELECT lhs AS rhs"
  :string)

(mr/def ::original-name
  "The original name of the column as it appeared in the very first place it came from (i.e., the physical name of the
  column in the table it appears in). This should be the same as the `:lib/source-column-alias` for the very first
  usage of the column.
  Allowed to be blank because some databases like SQL Server allow blank column names."
  [:maybe :string])

(mr/def ::deduplicated-name
  "The simply-deduplicated name that was historically used in QP results metadata (originally calculated by
  the [[metabase.query-processor.middleware.annotate]] middleware, now calculated
  by [[metabase.lib.middleware.result-metadata]]). This just adds suffixes to column names e.g. `ID` and `ID` become
  `ID` and `ID_2`, respectively. Kept around because many old field refs use this column name."
  [:maybe :string])

(defn- normalize-column [m]
  (when (map? m)
    (-> m
        lib.schema.common/normalize-map
        ;; sometimes column metadata from the FE has the NAME stored as the ID for hacky mystery purposes... ignore
        ;; these busted "IDs"
        (as-> m (cond-> m
                  (and (:id m) (not (pos-int? (:id m))))
                  (dissoc :id)))
        ;; If the column has a legacy `:source-alias` key but doesn't have `:lib/original-join-alias` then rename it
        ;; to that. `:source-alias` had a kind of ambiguous meaning before we removed it, but
        ;; `:lib/original-join-alias` was a subset of its purpose
        (as-> m (cond-> m
                  (and (:source-alias m)
                       (not (:lib/original-join-alias m)))
                  (set/rename-keys {:source-alias :lib/original-join-alias})))
        ;; Rename old long-namespaced keys (e.g. `:metabase.lib.field/temporal-unit`) to short `:lib/*` equivalents
        ;; for backwards compatibility with stored result_metadata
        lib.schema.common/rename-deprecated-lib-keys)))

(def ^:private column-validate-for-source-specs
  "Schemas to use to validate columns with a given `:lib/source`. Since a lot of these schemas are applicable to
  everything BUT one specific source it made more sense to write them out like this and use a bit of glue to build the
  schemas in [[column-validate-for-source-schema]]."
  [;; Only allow `:lib/expression-name` when `:lib/source` is `:source/expressions`. If it's anything else, it
   ;; probably means it's getting incorrectly propagated from a previous stage (QUE-1342).
   {:exclude :source/expressions
    :schema  (lib.schema.common/disallowed-keys
              {:lib/expression-name
               ":lib/expression-name should only be set for expressions in the current stage (i.e., columns with :source/expressions)"})}
   {:include :source/expressions
    :schema  [:fn
              {:error/message ":lib/expression-name is required for columns with :source/expressions"}
              :lib/expression-name]}
   ;; Current stage join alias (`:lib/join-alias`) should only be set for columns whose `:lib/source` is
   ;; `:source/joins`
   {:exclude :source/joins
    :schema  (lib.schema.common/disallowed-keys
              {:lib/join-alias
               (str "Current stage join alias (:lib/join-alias) should only be set for"
                    " columns joined in the current stage (i.e., columns with :source/joins).")})}
   ;; If source is `:source/joins` column must specify a current stage join alias.
   {:include :source/joins
    :schema  [:fn
              {:error/message (str "Columns joined in the current stage (i.e., columns with :source/joins) must specify"
                                   " current stage join alias (:lib/join-alias).")}
              (some-fn :lib/join-alias
                       ;; see [[metabase.lib.join/HACK-column-from-incomplete-join]]
                       :metabase.lib.join/HACK-from-incomplete-join?)]}
   ;; `:lib/source` `:source/implicitly-joinable` must have `:fk-field-id`; `:fk-field-id` is only allowed for
   ;; `:source/implicitly-joinable` and `:source/joins`. For columns from `:source/previous-stage` or whatever... in
   ;; that case we should be using `:lib/original-fk-field-id` instead.
   {:exclude #{:source/implicitly-joinable :source/joins}
    :schema  (lib.schema.common/disallowed-keys
              {:fk-field-id
               ":fk-field-id is only allowed for columns with :source/implicitly-joinable or :source/joins"})}
   {:include :source/implicitly-joinable
    :schema  [:fn
              {:error/message ":fk-field-id is required for columns with :source/implicitly-joinable"}
              :fk-field-id]}
   ;; columns with `:source/card` should have `:lib/card-id`. Doesn't matter if it gets propagated elsewhere
   {:include :source/card
    :schema  [:fn
              {:error/message "Columns with :source/card must have :lib/card-id"}
              :lib/card-id]}
   ;; columns with `:source/aggregations` or `:source/expressions` should always have `:lib/source-uuid`, since they
   ;; by definition come from an MBQL clause with a UUID in the query itself.
   ;;
   ;; TODO (Cam 8/7/25) -- disabled for now since this breaks a surprising amount of stuff.
   #_{:include #{:source/aggregations :source/expressions}
      :schema  [:fn
                {:error/message "Columns with :source/aggregations or :source/expressions must have :lib/source-uuid"}
                :lib/source-uuid]}])

(defn- column-validate-for-source-schema [source]
  (into [:and]
        (comp (remove (fn [validator]
                        (when-let [exclude (:exclude validator)]
                          (if (set? exclude)
                            (contains? exclude source)
                            (= exclude source)))))
              (filter (fn [validator]
                        (let [include (:include validator)]
                          (cond
                            (not include)  true
                            (set? include) (contains? include source)
                            :else          (= include source)))))
              (map :schema))
        column-validate-for-source-specs))

(mr/def ::column.validate-for-source-card                (column-validate-for-source-schema :source/card))
(mr/def ::column.validate-for-source-native              (column-validate-for-source-schema :source/native))
(mr/def ::column.validate-for-source-previous-stage      (column-validate-for-source-schema :source/previous-stage))
(mr/def ::column.validate-for-source-table-defaults      (column-validate-for-source-schema :source/table-defaults))
(mr/def ::column.validate-for-source-aggregations        (column-validate-for-source-schema :source/aggregations))
(mr/def ::column.validate-for-source-joins               (column-validate-for-source-schema :source/joins))
(mr/def ::column.validate-for-source-expressions         (column-validate-for-source-schema :source/expressions))
(mr/def ::column.validate-for-source-implicitly-joinable (column-validate-for-source-schema :source/implicitly-joinable))
(mr/def ::column.validate-for-source-pivot-grouping      (column-validate-for-source-schema :source/pivot-grouping))

(mr/def ::column.validate-for-source
  "Do additional validation for column metadata based on `:lib/source`."
  [:multi
   {:dispatch :lib/source}
   [:source/card                [:ref ::column.validate-for-source-card]]
   [:source/native              [:ref ::column.validate-for-source-native]]
   [:source/previous-stage      [:ref ::column.validate-for-source-previous-stage]]
   [:source/table-defaults      [:ref ::column.validate-for-source-table-defaults]]
   [:source/aggregations        [:ref ::column.validate-for-source-aggregations]]
   [:source/joins               [:ref ::column.validate-for-source-joins]]
   [:source/expressions         [:ref ::column.validate-for-source-expressions]]
   [:source/implicitly-joinable [:ref ::column.validate-for-source-implicitly-joinable]]
   [:source/pivot-grouping      [:ref ::column.validate-for-source-pivot-grouping]]
   [nil                         :any]])

(def column-visibility-types
  "Possible values for column `:visibility-type`."
  #{:normal       ; Default setting.  field has no visibility restrictions.
    :details-only ; For long blob like columns such as JSON.  field is not shown in some places on the frontend.
    :hidden       ; Lightweight hiding which removes field as a choice in most of the UI.  should still be returned in queries.
    :sensitive    ; Strict removal of field from all places except data model listing.  queries should error if someone attempts to access.
    :retired})    ; For fields that no longer exist in the physical db.  automatically set by Metabase.  QP should error if encountered in a query.

(mr/def ::column.visibility-type
  (into [:enum {:decode/normalize keyword}] column-visibility-types))

(def column-data-sensitivity-types
  "Possible values for column `:data-sensitivity`, most severe first. The order is the precedence an automated
  classifier uses when several categories match a single column. `nil` means the column has never been scanned or
  labeled; `:PUBLIC` means it was scanned or reviewed and nothing sensitive was found. Whether a person made the call
  is recorded by the presence of a value in `metabase_field_user_settings`, not by the value itself."
  [:SEC_KEY       ; Security Credentials & Secrets
   :SYS_TELEMETRY ; Infrastructure & System Secrets
   :PHI           ; Protected Health Information
   :BIO_GEN       ; Biometric & Genetic Data
   :PCI_FIN       ; Financial & Payment Card Data
   :SENS_PERS     ; Special Category Personal Traits
   :PII           ; Personally Identifiable Information
   :CORP_IP       ; Intellectual Property & Code
   :BIZ_CONF      ; Confidential Business Data
   :PUBLIC])      ; Non-Sensitive / Public Information

(mr/def ::column.data-sensitivity
  (into [:enum {:decode/normalize keyword}] column-data-sensitivity-types))

(mr/def ::column.legacy-source
  "Possible values for `column.source` -- this is added by [[metabase.lib.metadata.result-metadata]] for historical
  reasons (it is used in a few places in the FE). DO NOT use this in the backend for any purpose, use `:lib/source`
  instead."
  [:enum {:decode/normalize keyword} :aggregation :fields :breakout :native])

(mr/def ::column.snapshot
  "A whole column metadata map stashed on *another* column under one of the internal keys below: the same shape as
  [[::column]] minus the `:and`-level constraints, so validating the stashed copy does not re-run those constraints
  against the column it was copied from."
  [:ref ::column.map])

(mr/def ::column.timestamp
  "A timestamp column of a `metabase_field` row: a `java.time` value on the JVM, the string it was encoded as elsewhere."
  #?(:clj  [:or :string (lib.schema.common/instance-of-class java.time.temporal.Temporal)]
     :cljs :string))

(mr/def ::column.options
  "What the query processor stashes under a column's `:options` while it runs: never sent by a client, and never
  handed back to one."
  [:map {:closed true, :decode/api lib.schema.common/remove-internal-keys, :encode/serialize lib.schema.common/remove-internal-keys}
   [:qp/ignore-coercion                                {:optional true} :boolean]
   [:qp/native-sandbox-column.force-coercion-strategy  {:optional true} [:maybe ::lib.schema.common/coercion-strategy]]
   [:qp/native-sandbox-column.propagate-coercion?      {:optional true} [:maybe :boolean]]
   [:metabase.query-processor.util.add-alias-info/source-table  {:optional true} [:maybe [:or :int :string :keyword]]]
   [:metabase.query-processor.util.add-alias-info/source-alias  {:optional true} [:maybe :string]]
   [:metabase.query-processor.util.add-alias-info/desired-alias {:optional true} [:maybe :string]]
   [:metabase.query-processor.middleware.add-remaps/original-field-dimension-id {:optional true} [:maybe ::lib.schema.id/dimension]]
   [:metabase.query-processor.middleware.add-remaps/new-field-dimension-id      {:optional true} [:maybe ::lib.schema.id/dimension]]
   [:metabase.query-processor.util.transformations.nest-breakouts/externally-remapped-field {:optional true} :boolean]])

(mr/def ::column.lib-options
  "What [[metabase.lib.options/with-options]] puts under `:lib/options` when it is handed a column rather than a
  clause: the expression name [[metabase.lib.fe-util/expression-parts]] pins on an expression column, and a `:lib/uuid`
  if something asked for one."
  [:map {:closed true}
   [:lib/uuid            {:optional true} ::lib.schema.common/uuid]
   [:lib/expression-name {:optional true} ::lib.schema.common/non-blank-string]])

(mr/def ::column.binning-info
  "The legacy description of how a column was binned, as [[metabase.lib.metadata.result-metadata]] spells it out for
  the frontend."
  [:map {:closed true}
   [:strategy         {:optional true} [:maybe [:ref ::lib.schema.binning/strategy]]]
   [:binning-strategy {:optional true} [:maybe [:ref ::lib.schema.binning/strategy]]]
   [:binning_strategy {:optional true} [:maybe [:ref ::lib.schema.binning/strategy]]]
   [:num-bins         {:optional true} [:maybe :int]]
   [:num_bins         {:optional true} [:maybe :int]]
   [:bin-width        {:optional true} [:maybe number?]]
   [:bin_width        {:optional true} [:maybe number?]]
   [:min-value        {:optional true} [:maybe number?]]
   [:min_value        {:optional true} [:maybe number?]]
   [:max-value        {:optional true} [:maybe number?]]
   [:max_value        {:optional true} [:maybe number?]]])

(def ^:private column-map
  [:map
   {:closed           true
    :error/message    "Valid column metadata"
    :decode/normalize normalize-column
    :decode/api       lib.schema.common/remove-internal-keys
    :encode/serialize lib.schema.common/remove-internal-keys}
   [:lib/type  [:= {:decode/normalize lib.schema.common/normalize-keyword, :default :metadata/column} :metadata/column]]
   ;;
   ;; TODO (Cam 6/19/25) -- change all these comments to proper `:description`s like we have
   ;; in [[metabase.legacy-mbql.schema]] so we can generate this documentation from this schema or whatever.
   ;;
   ;; column names are allowed to be empty strings in SQL Server :/
   ;;
   ;; In almost EVERY circumstance you should try to avoid using `:name`, because it's not well-defined whether it's
   ;; the `:lib/original-name` or `:lib/deduplicated-name`, and it might be either one depending on where the metadata
   ;; came from. Prefer one of the other name keys instead, only falling back to `:name` if they are not present.
   [:name      :string]
   ;; TODO -- ignore `base_type` and make `effective_type` required; see #29707
   [:base-type {:default :type/*} ::lib.schema.common/base-type]
   ;; This is nillable because internal remap columns have `:id nil`.
   [:id             {:optional true} [:maybe ::lib.schema.id/field]]
   [:display-name   {:optional true} [:maybe :string]]
   [:effective-type {:optional true} [:maybe ::lib.schema.common/base-type]]
   [:semantic-type  {:optional true} [:maybe ::lib.schema.common/semantic-or-relation-type]]
   ;; type of this column in the data warehouse, e.g. `TEXT` or `INTEGER`
   [:database-type  {:optional true} [:maybe :string]]
   [:active         {:optional true} :boolean]
   [:visibility-type {:optional true} [:maybe ::column.visibility-type]]
   [:data-sensitivity {:optional true} [:maybe ::column.data-sensitivity]]
   ;; if this is a field from another table (implicit join), this is the field in the current table that should be
   ;; used to perform the implicit join. e.g. if current table is `VENUES` and this field is `CATEGORIES.ID`, then the
   ;; `fk_field_id` would be `VENUES.CATEGORY_ID`. In a `:field` reference this is saved in the options map as
   ;; `:source-field`.
   [:fk-field-id {:optional true} [:maybe ::lib.schema.id/field]]
   ;; if this is a field from another table (implicit join), this is the name of the source field. It can be either a
   ;; `:lib/desired-column-alias` or `:name`, depending on the `:lib/source`. It's set only when the field can be
   ;; referenced by a name, normally when it's coming from a card or a previous query stage.
   [:fk-field-name {:optional true} [:maybe :string]]
   ;; if this is a field from another table (implicit join), this is the join alias of the source field.
   [:fk-join-alias {:optional true} [:maybe ::lib.schema.join/alias]]
   ;; `metabase_field.fk_target_field_id` in the application database; recorded during the sync process. This Field is
   ;; an foreign key, and points to this Field ID. This is mostly used to determine how to add implicit joins by
   ;; the [[metabase.query-processor.middleware.add-implicit-joins]] middleware.
   [:fk-target-field-id {:optional true} [:maybe ::lib.schema.id/field]]
   ;; Join alias of the table we're joining against, if any. SHOULD ONLY BE SET IF THE JOIN HAPPENED AT THIS STAGE OF
   ;; THE QUERY! (Also ok within a join's conditions for previous joins within the parent stage, because a join is
   ;; allowed to join on the results of something else)
   [:lib/join-alias {:optional true} [:maybe ::lib.schema.join/alias]]
   ;; the initial join alias used when this column was first introduced; should be propagated even if the join was
   ;; from a previous stage.
   ;;
   ;; What about when the column comes from join `X`, but inside `X` itself it comes from join `Y`? I think in this
   ;; case we want the outside world to see `X` since `Y` is not visible outside of `X`.
   ;;
   ;;    original join alias = X
   ;;    column => [join X => join Y]
   ;;
   ;; It is not currently well-defined whether this appears when the join was the current stage or not, i.e. if it's
   ;; equal to `:lib/join-alias` when it is set or if it is only set if the join happened in a previous
   ;; stage, i.e. if it's `nil` when `:lib/join-alias` is set. It seems like current behavior is the
   ;; former but you should NOT rely on this behavior.
   [:lib/original-join-alias {:optional true} [:maybe ::lib.schema.join/alias]]
   ;; these should only be present if temporal bucketing or binning is done in the current stage of the query; if
   ;; this happened in a previous stage they should get propagated as the keys below instead.
   [:lib/temporal-unit {:optional true} [:maybe ::lib.schema.temporal-bucketing/unit]]
   [:lib/binning       {:optional true} [:maybe ::lib.schema.binning/binning]]
   ;; For nested fields (fields with a `:parent-id`, e.g. JSON columns), the pre-computed display name including the
   ;; full parent chain prefix, e.g. `"Grandparent: Parent: Child"`. Used as the highest-priority initial display
   ;; name in the display name pipeline, before join alias, binning, and temporal bucketing decorations are added.
   ;; This is distinct from `:lib/original-display-name`, which for a nested field stores just the leaf name
   ;; (e.g. `"Child"`) — both may coexist on the same column.
   [:lib/simple-display-name {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:lib/original-effective-type {:optional true} [:maybe ::lib.schema.common/base-type]]
   [:lib/transformation-added-base-type {:optional true} [:maybe :boolean]]
   ;; If temporal bucketing or binning happened in a previous stage, they are propagated as the keys below.
   ;; `:inherited-temporal-unit` signals that this column was already bucketed upstream, so the default temporal
   ;; unit becomes `:inherited` rather than a type-based default like `:month`, preventing double-bucketing.
   [:inherited-temporal-unit {:optional true} [:maybe ::lib.schema.temporal-bucketing/unit]]
   [:lib/original-binning    {:optional true} [:maybe ::lib.schema.binning/binning]]
   ;; name of the expression where this column metadata came from. Should only be included for expressions introduced
   ;; at THIS STAGE of the query. If it's included elsewhere, that's an error. Thus this is the definitive way to know
   ;; if a column is "custom" in this stage (needs an `:expression` reference) or not.
   [:lib/expression-name {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   ;;
   ;; the name of the expression where this column came from, if the column came from a previous stage of the query
   [:lib/original-expression-name {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   ;; where this column came from. See docstring for `::column.source`.
   [:lib/source {:optional true} [:maybe [:ref ::column.source]]]
   ;;
   ;; if this column metadata was calculated based on MBQL clauses in the query itself, this is the UUID of the
   ;; clauses in question. Required for aggregations and expressions
   [:lib/source-uuid {:optional true} [:maybe [:ref ::lib.schema.common/uuid]]]
   ;;
   ;; whether this column metadata occurs in the `:breakout`(s) in the CURRENT STAGE or not. Previously this was
   ;; signified by `:lib/source = :source/breakouts` (which has been removed)
   ;;
   ;; this SHOULD NOT get propagated to subsequent stages!
   [:lib/breakout? {:optional true} [:maybe :boolean]]
   ;;
   ;; ID of the Card this came from, if this column originally came from a Card (Saved Question or Model). Mostly
   ;; used for creating column groups. AFAIK this should get propagated indefinitely -- Cam
   [:lib/card-id {:optional true} [:maybe ::lib.schema.id/card]]
   ;;
   ;; Whether this column originally was introduced by a Model. Model metadata has special rules, for example because
   ;; `:display-name` can be user-edited we should be careful not to recalculate it unnecessarily. See for
   ;; example [[metabase.query-processor.model-test/preserve-model-display-names-test]].
   ;;
   ;; This key should get propagated indefinitely.
   [:lib/from-model? {:optional true} [:maybe :boolean]]
   ;;
   ;; this stuff is adapted from [[metabase.query-processor.util.add-alias-info]]. It is included in
   ;; the [[metabase.lib.metadata.calculation/metadata]]
   ;;
   ;; the alias that should be used to this clause on the LHS of a `SELECT <lhs> AS <rhs>` or equivalent, i.e. the
   ;; name of this clause as exported by the previous stage, source table, or join.
   [:lib/source-column-alias {:optional true} [:maybe ::source-column-alias]]
   ;; the name we should export this column as, i.e. the RHS of a `SELECT <lhs> AS <rhs>` or equivalent. This is
   ;; guaranteed to be unique in each stage of the query.
   [:lib/desired-column-alias {:optional true} [:maybe ::desired-column-alias]]
   ;;
   ;; see description in schemas above
   ;;
   [:lib/original-name     {:optional true} ::original-name]
   [:lib/deduplicated-name {:optional true} ::deduplicated-name]
   ;;
   ;; the original display name of this column before adding join/temporal bucketing/binning stuff to it. `Join ->
   ;; <whatever>` or `<whatever>: Month` or `<whatever>: Auto-binned`. Should be equal to the very first
   ;; `:display-name` we see when the column comes out of a metadata provider. Usually this is auto-generated with
   ;; humanized names from `:name`, but may differ.
   ;;
   ;; For model columns, this is set to the clean column name from the model's result_metadata with any
   ;; join arrow prefix stripped (e.g. "Products → Category" becomes "Category").
   [:lib/original-display-name {:optional true} [:maybe :string]]
   ;;
   ;; If this metadata was resolved from a ref (e.g. a `:field` ref) that contained a `:display-name` in the options,
   ;; this is that display name. `:lib/ref-display-name` should override any display names specified in the metadata.
   [:lib/ref-display-name {:optional true} [:maybe :string]]
   ;;
   ;; If this metadata was resolved from a ref (e.g. a `:field` ref) and that ref contained a `:name` in the options,
   ;; this is that name. If specified we should use this as the basis for the desired column alias rather than join
   ;; alias + source column alias.
   [:lib/ref-name {:optional true} [:maybe :string]]
   ;;
   ;; when column metadata is returned by certain things
   ;; like [[metabase.lib.aggregation/selected-aggregation-operators]] or [[metabase.lib.field/fieldable-columns]], it
   ;; might include this key, which tells you whether or not that column is currently selected or not already, e.g.
   ;; for [[metabase.lib.field/fieldable-columns]] it means its already present in `:fields`
   [:selected? {:optional true} :boolean]
   ;;
   ;; REMAPPING & FIELD VALUES
   ;;
   ;; See notes above for more info. `:has-field-values` comes from the application database and is used to decide
   ;; whether to sync FieldValues when running sync, and what certain FE QB widgets should
   ;; do. (See [[metabase.lib.field/field-values-search-info]]). Note that all metadata providers may not return this
   ;; column. The JVM provider currently does not, since the QP doesn't need it for anything.
   [:has-field-values {:optional true} [:maybe [:ref ::column.has-field-values]]]
   ;;
   ;; info about stuff like min and max values of the column, used for auto bucketing.
   [:fingerprint {:optional true} [:maybe [:ref ::lib.schema.metadata.fingerprint/fingerprint]]]
   ;;
   ;; If this is a nested column, the names of the ancestor columns used to access it. E.g. if the column is
   ;; `grandparent.parent.child` then `:nfc-path` would be `["grandparent" "parent"]`.
   ;;
   ;; This was originally added to power Postgres JSON column support; but is now used for any sort of nested column,
   ;; including BigQuery `RECORD` columns and MongoDB nested columns. See
   ;; https://metaboat.slack.com/archives/C0645JP1W81/p1754949404592539 for code archeology
   [:nfc-path {:optional true} [:maybe [:sequential :string]]]
   ;; the rest of the `metabase_field` row a metadata provider hands back
   [:table-id                   {:optional true} [:maybe [:or ::lib.schema.id/table [:re #"^card__\d+$"]]]]
   [:parent-id                  {:optional true} [:maybe ::lib.schema.id/field]]
   [:description                {:optional true} [:maybe :string]]
   [:caveats                    {:optional true} [:maybe :string]]
   [:points-of-interest         {:optional true} [:maybe :string]]
   [:coercion-strategy          {:optional true} [:maybe ::lib.schema.common/coercion-strategy]]
   [:position                   {:optional true} [:maybe :int]]
   [:custom-position            {:optional true} [:maybe :int]]
   [:database-position          {:optional true} [:maybe :int]]
   [:database-default           {:optional true} [:maybe :string]]
   [:database-indexed           {:optional true} [:maybe :boolean]]
   [:database-is-auto-increment {:optional true} [:maybe :boolean]]
   [:database-is-generated      {:optional true} [:maybe :boolean]]
   [:database-is-nullable       {:optional true} [:maybe :boolean]]
   [:database-is-pk             {:optional true} [:maybe :boolean]]
   [:database-partitioned       {:optional true} [:maybe :boolean]]
   [:database-required          {:optional true} [:maybe :boolean]]
   [:json-unfolding             {:optional true} [:maybe :boolean]]
   [:preview-display            {:optional true} [:maybe :boolean]]
   [:fingerprint-version        {:optional true} [:maybe :int]]
   [:created-at                 {:optional true} [:maybe [:ref ::column.timestamp]]]
   [:updated-at                 {:optional true} [:maybe [:ref ::column.timestamp]]]
   [:last-analyzed              {:optional true} [:maybe [:ref ::column.timestamp]]]
   ;; the Field a foreign key points at, and the Field that names this table's rows: hydrated onto a Field by the
   ;; application database metadata provider, but only an id reference in the metadata the frontend hands over
   [:target                     {:optional true} [:maybe [:or ::lib.schema.id/field [:ref ::column]]]]
   [:name-field                 {:optional true} [:maybe [:or ::lib.schema.id/field [:ref ::column]]]]
   ;; the Dimension and FieldValues joined onto a Field by the application database metadata provider
   [:dimension/id                      {:optional true} [:maybe ::lib.schema.id/dimension]]
   [:dimension/name                    {:optional true} [:maybe :string]]
   [:dimension/type                    {:optional true} [:maybe [:enum {:decode/normalize lib.schema.common/normalize-keyword} :internal :external]]]
   [:dimension/human-readable-field-id {:optional true} [:maybe ::lib.schema.id/field]]
   [:values/values                     {:optional true} [:maybe [:sequential [:ref ::lib.schema.literal/literal]]]]
   [:values/human-readable-values      {:optional true} [:maybe [:sequential [:ref ::lib.schema.literal/literal]]]]
   [:field-values                      {:optional true} [:maybe [:sequential ::lib.schema.common/field-value]]]
   ;; what Lib annotates a column with when it returns it from a query
   [:breakout-positions  {:optional true} [:maybe [:sequential :int]]]
   [:filter-positions    {:optional true} [:maybe [:sequential :int]]]
   [:order-by-position   {:optional true} [:maybe :int]]
   [:converted-timezone  {:optional true} [:maybe :string]]
   [:remapped-from       {:optional true} [:maybe :string]]
   [:remapped-to         {:optional true} [:maybe :string]]
   [:remapped-from-index {:optional true} [:maybe :int]]
   [:remapping           {:optional true} [:maybe [:map-of [:ref ::lib.schema.literal/literal] [:ref ::lib.schema.literal/literal]]]]
   [:dimension-interestingness {:optional true} [:maybe number?]]
   [:options             {:optional true} [:maybe [:ref ::column.options]]]
   [:lib/options                 {:optional true} [:maybe [:ref ::column.lib-options]]]
   [:lib/original-fk-field-id    {:optional true} [:maybe ::lib.schema.id/field]]
   [:lib/original-fk-field-name  {:optional true} [:maybe :string]]
   [:lib/original-fk-join-alias  {:optional true} [:maybe ::lib.schema.join/alias]]
   [:lib/original-join-name      {:optional true} [:maybe :string]]
   [:lib/source-display-name     {:optional true} [:maybe :string]]
   [:metabase.lib.join/HACK-from-incomplete-join? {:optional true} [:maybe :boolean]]
   ;; where the Mongo driver keeps the aliases of a `:field` ref while it compiles the ref as a column
   [:metabase.driver.mongo.query-processor/source-alias {:optional true} [:maybe :string]]
   [:metabase.driver.mongo.query-processor/join-field   {:optional true} [:maybe :string]]
   [:metabase.driver.mongo.query-processor/inherited?   {:optional true} [:maybe :boolean]]
   ;;
   ;; `:unit` and `:binning-info` are legacy keys and should not be set on a Lib column; `:lib/temporal-unit` and
   ;; `:lib/binning` are the keys for that. [[metabase.lib.metadata.result-metadata]] pins these two on a column on its
   ;; way to becoming legacy result metadata, which is the only reason they are declared.
   [:unit                {:optional true} [:maybe [:ref ::lib.schema.temporal-bucketing/unit]]]
   [:binning-info        {:optional true} [:maybe [:ref ::column.binning-info]]]
   ;;
   ;; populated by the `metabase_field.settings` column in the application database; I'm not really sure what goes in
   ;; here and if it's actually used for anything important in Lib or the QP (I suspect it's not).
   [:settings {:optional true} [:maybe [:ref ::lib.schema.common/visualization-settings]]]
   ;;
   ;; Added by [[metabase.lib.metadata.result-metadata]] primarily for legacy/backward-compatibility purposes with
   ;; legacy viz settings. This should not be used for anything other than that.
   [:metabase.lib.metadata.result-metadata/field-ref
    {:optional true}
    [:maybe #?(:cljs [:or
                      [:ref :metabase.legacy-mbql.schema/Reference]
                      [:fn {:error/message "JS array"}
                       array?]]
               :clj  [:ref :metabase.legacy-mbql.schema/Reference])]]
   ;;
   [:metabase.lib.metadata.result-metadata/source {:optional true} [:maybe [:ref ::column.legacy-source]]]
   ;;
   ;; these next two keys are derived by looking at `FieldValues` and `Dimension` instances associated with a `Field`;
   ;; they are used by the Query Processor to add column remappings to query results. To see how this maps to stuff in
   ;; the application database, look at the implementation for fetching a `:metadata/column`
   ;; in [[metabase.lib-be.metadata.jvm]]. I don't think this is really needed on the FE, at any rate the JS metadata
   ;; provider doesn't add these keys.
   [:lib/external-remap {:optional true} [:maybe [:ref ::column.remapping.external]]]
   [:lib/internal-remap {:optional true} [:maybe [:ref ::column.remapping.internal]]]
   ;;
   ;; The [[metabase.query-processor.middleware.add-implicit-clauses/add-implicit-fields]] middleware adds
   ;; `:qp/added-implicit-fields?` to stages where it adds implicit fields,
   ;; then [[metabase.lib.stage/fields-columns]] adds this key to any col from such a
   ;; stage. [[metabase.lib.metadata.result-metadata/super-broken-legacy-field-ref]] uses this to know to force Field
   ;; ID refs for QP `:field_ref` in results metadata to preserve historic behavior to avoid breaking legacy viz
   ;; settings that use it as a key.
   [:qp/implicit-field? {:optional true} [:maybe :boolean]]
   ;;
   ;; Coercion strategies (eg. UNIX seconds -> :type/DateTime) are configured on :model/Fields in the Admin UI, and
   ;; reflected on refs to that field on the *first* stage of an MBQL query or a join, where the column first appears
   ;; in the query. After the column passes through a stage boundary, it's no longer marked with the coercion strategy
   ;; to avoid double-coercion.
   ;;
   ;; *However*, when a table is sandboxed by a native query, and it has fields which need coercion, the SQL subquery
   ;; does not do the coercion (it's supposed to be a drop-in replacement for the table) but then the MBQL refs to its
   ;; columns are not in the first stage anymore! So the sandboxing middleware sets this flag to the
   ;; `:coercion-strategy`, along with `:qp/native-sandbox-column.propagate-coercion? true` (see below). Lib will
   ;; propagate the coercion strategy through *exactly one* stage boundary, so it can get from the SQL first stage to
   ;; the earliest MBQL stage, where the coercion will get applied correctly. See QUE2-376 or #69867 for more details.
   [:qp/native-sandbox-column.force-coercion-strategy {:optional true} [:ref ::lib.schema.common/coercion-strategy]]
   ;;
   ;; See above about `:qp/native-sandbox-column.force-coercion-strategy`.
   [:qp/native-sandbox-column.propagate-coercion? {:optional true} :boolean]
   [:metabase.lib.metadata.result-metadata/remove-join-alias? {:optional true} :boolean]
   [:lib/original-ref-style-for-result-metadata-purposes
    {:optional true}
    [:enum :original-ref-style/id :original-ref-style/name]]
   [:metabase.lib.field.resolution/fallback-metadata? {:optional true} :boolean]
   [:metabase.lib.join/target {:optional true} [:ref ::column.snapshot]]
   [:metabase.lib.underlying/original      {:optional true} [:ref ::column.snapshot]]
   [:metabase.lib.underlying/temporal-unit {:optional true} [:maybe [:ref ::lib.schema.temporal-bucketing/unit]]]
   [:metabase.lib.underlying/binning       {:optional true} [:maybe [:ref ::lib.schema.binning/binning]]]
   [:metabase.query-processor.pivot/idx {:optional true} [:int {:min 0}]]])

(mr/def ::column.map
  "The map portion of [[::column]], without its constraints."
  column-map)

(mr/def ::column
  "Malli schema for a valid map of column metadata, which can mean one of two things:

  1. Metadata about a particular Field in the application database. This will always have an `:id`

  2. Results metadata from a column in `data.cols` and/or `data.results_metadata.columns` in a Query Processor
     response, or saved in something like `Card.result_metadata`. These *may* have an `:id`, or may not -- columns
     coming back from native queries or things like `SELECT count(*)` aren't associated with any particular `Field`
     and thus will not have an `:id`.

  Now maybe these should be two different schemas, but `:id` being there or not is the only real difference; besides
  that they are largely compatible. So they're the same for now. We can revisit this in the future if we actually want
  to differentiate between the two versions."
  [:and
   column-map
   ;;
   ;; Additional constraints
   ;;
   [:ref ::lib.schema.common/kebab-cased-map]
   [:ref ::column.validate-for-source]
   (lib.schema.common/disallowed-keys
    (into {:binning           ":binning is deprecated; use :lib/binning instead"
           :field-ref         ":field-ref is deprecated. For QP result metadata, use :metabase.lib.metadata.result-metadata/field-ref"
           :ident             ":ident is deprecated and should not be included in column metadata"
           :lib/model-display-name ":lib/model-display-name is deprecated and should not be included in column metadata"
           :model/inner-ident ":model/inner_ident (normalized to :model/inner-ident) is deprecated and should not be included in column metadata"
           :source            ":source is deprecated; use :lib/source instead. For QP result metadata, use :metabase.lib.metadata.result-metadata/source"
           :source-alias      ":source-alias is deprecated; use :lib/join-alias or :lib/original-join-alias instead"
           :unit              ":unit is deprecated; use :lib/temporal-unit instead"}
          (map (fn [[old-key new-key]]
                 [old-key (str old-key " is deprecated; use " new-key " instead")]))
          lib.schema.common/deprecated-lib-key-renames))])

(mr/def ::persisted-info.definition
  "Definition spec for a cached table."
  [:map {:closed true}
   [:table-name        ::lib.schema.common/non-blank-string]
   [:field-definitions [:maybe [:sequential
                                [:map {:closed true}
                                 [:field-name ::lib.schema.common/non-blank-string]
                                 ;; TODO check (isa? :type/Integer :type/*)
                                 [:base-type  ::lib.schema.common/base-type]]]]]])

(mr/def ::persisted-info
  "Persisted Info = Cached Table (?). See [[metabase.model-persistence.models.persisted-info]]"
  [:map {:closed true}
   [:active     :boolean]
   [:state      ::lib.schema.common/non-blank-string]
   [:table-name ::lib.schema.common/non-blank-string]
   [:definition {:optional true} [:maybe [:ref ::persisted-info.definition]]]
   [:query-hash {:optional true} [:maybe ::lib.schema.common/non-blank-string]]])

(def card-types
  "Valid Card `:type`s."
  #{:question :model :metric})

(mr/def ::card.type
  "All acceptable card types.

  Previously (< 49), we only had 2 card types: question and model, which were differentiated using the boolean
  `dataset` column. Soon we'll have more card types (e.g: metric) and we will longer be able to use a boolean column
  to differentiate between all types. So we've added a new `type` column for this purpose.

  Migrating all the code to use `report_card.type` will be quite an effort, we decided that we'll migrate it
  gradually."
  (into [:enum
         (merge
          {:decode/json      lib.schema.common/normalize-keyword
           :decode/normalize lib.schema.common/normalize-keyword}
          #?(:clj
             {:api/regex (u.regex/re-or (map name card-types))}))]
        card-types))

(mr/def ::type
  "TODO -- not convinced we need a separate `:metadata/metric` anymore, it made sense back when Legacy/V1 Metrics were a
  separate table in the app DB, but now that they're a subtype of Card it's probably not important anymore, we can
  probably just use `:metadata/card` here."
  [:enum :metadata/database :metadata/table :metadata/column :metadata/card :metadata/metric :metadata/measure
   :metadata/segment :metadata/native-query-snippet])

(mr/def ::lib-or-legacy-column
  "Schema for the maps in card `:result-metadata` and similar. These can be either
  `:metabase.lib.schema.metadata/result-metadata` (i.e., kebab-cased) maps, or map snake_cased as returned by QP
  metadata, but they should NOT be a mixture of both -- if we mixed them somehow there is a bug in our code."
  [:multi
   {:dispatch (fn [col]
                ;; if this has `:lib/type` we know FOR SURE that it's lib-style metadata; but we should also be able
                ;; to infer this fact automatically if it's using `kebab-case` keys. `:base-type` is required for both
                ;; styles so look at that.
                (if ((some-fn :lib/type #(get % "lib/type") :base-type #(get % "base-type")) col)
                  :lib
                  :legacy))}
   [:lib
    [:merge
     [:ref ::column]
     [:map
      {:error/message "If a Card result metadata column has :lib/type it MUST be a valid kebab-cased :metabase.lib.schema.metadata/column"}]]]
   [:legacy
    [:ref :metabase.legacy-mbql.schema/legacy-column-metadata]]])

;;; TODO (Cam 2026-05-26) check whether we can change `::card.query` to point to `:metabase.lib.schema/query` now that
;;; Cards should always have MBQL 5

(defn- normalize-card-query [query]
  (when query
    (let [query (lib.schema.common/normalize-map query)]
      ;; we will do more normalization/conversion later.
      (-> query
          (m/update-existing :type keyword)
          (m/update-existing :lib/type keyword)))))

(mr/def ::card.query
  "Saved query. This is possibly still a legacy query, but should already be normalized.
  Call [[metabase.lib.convert/->mbql5]] on it as needed."
  ;; dispatched rather than written as a keyless `[:map {:decode/normalize ...}]`, which declares no keys and so
  ;; would strip the whole query while decoding
  [:multi {:dispatch (fn [query]
                       (cond
                         (empty? query)              :empty
                         (:lib/type query)           :mbql5
                         :else                       :legacy))}
   ;; Cards may be saved with an empty query -- see `:metabase.queries.schema/query`
   [:empty  [:= {} {}]]
   [:mbql5  [:schema
             {:decode/normalize normalize-card-query}
             [:ref :metabase.lib.schema/query]]]
   [:legacy [:schema
             {:decode/normalize normalize-card-query}
             [:ref :metabase.legacy-mbql.schema/Query]]]])

(defn- normalize-card [card]
  (when card
    (let [card (lib.schema.common/normalize-map card)]
      (cond-> card
        (and (not (:database-id card))
             (pos-int? (get-in card [:dataset-query :database])))
        (assoc :database-id (get-in card [:dataset-query :database]))))))

(defn- mock-card
  "Mock coercer for use with the [[metabase.lib.test-util/mock-metadata-provider]]. Add a default name to the Card if
  one was not specified."
  [card]
  (cond-> card
    (and (not (:name card))
         (:id card))
    (assoc :name (str "Card " (:id card)))

    (not (:collection-id card))
    (assoc :collection-id nil)))

(mr/def ::card.result-metadata
  "Schema for the `:result-metadata` for a Card (Saved Question, Model, or v2 Metric)."
  ;; TODO (Cam 2026-02-11) consider whether we should add additional constraints here like distinct `:name`s... we can
  ;; fix these with normalization if needed
  [:sequential [:ref ::lib-or-legacy-column]])

(mr/def ::card
  "Schema for metadata about a specific Saved Question (which may or may not be a Model). More or less the same as
  a [[metabase.queries.models.card]], but with kebab-case keys. Note that the `:dataset-query` is not necessarily
  converted to MBQL 5 yet. Probably safe to assume it is normalized however. Likewise, `:result-metadata` is probably
  not quite massaged into a sequence of [[::column]] metadata just yet.

  See [[metabase.lib.card/card-metadata-columns]] that converts these as needed."
  [:map
   {:closed true, :decode/normalize normalize-card
    :decode/mock      mock-card
    :error/message    "Valid Card metadata"}
   [:lib/type    [:= :metadata/card]]
   [:id          ::lib.schema.id/card]
   [:name        ::lib.schema.common/non-blank-string]
   [:database-id ::lib.schema.id/database]
   [:dataset-query   {:optional true} ::card.query]
   ;; vector of column metadata maps; these are ALMOST the correct shape to be [[ColumnMetadata]], but they're
   ;; probably missing `:lib/type` and probably using `:snake_case` keys.
   [:result-metadata {:optional true} [:maybe [:ref ::card.result-metadata]]]
   ;; what sort of saved query this is, e.g. a normal Saved Question or a Model or a V2 Metric.
   [:type            {:optional true} [:maybe [:ref ::card.type]]]
   ;; Table ID is nullable in the application database, because native queries are not necessarily associated with a
   ;; particular Table (unless they are against MongoDB)... for MBQL queries it should be populated however.
   [:table-id        {:optional true} [:maybe ::lib.schema.id/table]]
   ;; ID of the collection this Card is saved in. `nil` means it is saved in the "Root Collection". Important for
   ;; perms-checking purposes.
   [:collection-id   {:optional true} [:maybe ::lib.schema.id/collection]]
   [:entity-id              {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:created-at             {:optional true} [:maybe [:or :string #?(:clj (lib.schema.common/instance-of-class java.time.temporal.Temporal))]]]
   [:card-schema            {:optional true} [:maybe :int]]
   [:visualization-settings {:optional true} [:maybe [:ref ::lib.schema.common/visualization-settings]]]
   [:description            {:optional true} [:maybe :string]]
   [:display                {:optional true} [:maybe [:or :keyword :string]]]
   [:archived               {:optional true} [:maybe :boolean]]
   [:source-card-id         {:optional true} [:maybe ::lib.schema.id/card]]
   [:dashboard-id           {:optional true} [:maybe pos-int?]]
   [:dimensions             {:optional true} [:maybe [:sequential [:ref ::persisted-dimension]]]]
   [:dimension-mappings     {:optional true} [:maybe [:sequential [:ref ::dimension-mapping]]]]
   [:display-name           {:optional true} [:maybe :string]]
   [:fields                 {:optional true} [:maybe [:ref ::card.result-metadata]]]
   ;;
   ;; PERSISTED INFO: This comes from the [[metabase.model-persistence.models.persisted-info]] model.
   ;;
   [:lib/persisted-info {:optional true} [:maybe [:ref ::persisted-info]]]])

(defn- mock-segment [segment]
  (cond-> segment
    (and (not (:name segment))
         (:id segment))
    (assoc :name (str "Segment " (:id segment)))))

(mr/def ::segment.definition
  "Segment definition: empty, an MBQL 5 query, or a legacy inner query."
  [:multi {:dispatch (fn [definition]
                       (cond
                         (empty? definition)      :empty
                         (:lib/type definition)   :mbql5
                         :else                    :legacy))}
   [:empty  [:= {} {}]]
   [:mbql5  [:ref :metabase.lib.schema/query]]
   [:legacy [:ref :metabase.legacy-mbql.schema/MBQLInnerQuery]]])

(mr/def ::segment
  "More or less the same as a [[metabase.segments.models.segment]], but with kebab-case keys."
  [:map
   {:closed true, :error/message "Valid Segment metadata"
    :decode/mock   mock-segment}
   [:lib/type   [:= :metadata/segment]]
   [:id         ::lib.schema.id/segment]
   [:name       ::lib.schema.common/non-blank-string]
   [:table-id   ::lib.schema.id/table]
   ;; the MBQL snippet defining this Segment; this may still be in legacy
   ;; format. [[metabase.lib.segment/segment-definition]] handles conversion to MBQL 5 if needed.
   [:definition [:maybe [:ref ::segment.definition]]]
   [:description {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:entity-id   {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:archived    {:optional true} [:maybe :boolean]]
   [:filter-positions {:optional true} [:maybe [:sequential :int]]]])

(defn- normalize-measure-definition [definition]
  (when definition
    (lib.schema.common/normalize-map definition)))

(mr/def ::measure.definition
  "Measure definition query. This should be an MBQL5 query with a single stage and one aggregation.
   Strict validation via :metabase.lib.schema.measure/definition happens in metabase.measures.models.measure."
  ;; wrapped in `[:schema ...]` rather than written as a keyless `[:map {:decode/normalize ...}]`, which declares no
  ;; keys and so would strip the whole definition while decoding
  [:schema
   {:decode/normalize normalize-measure-definition}
   [:ref :metabase.lib.schema/query]])

(defn- mock-measure [measure]
  (cond-> measure
    (and (not (:name measure))
         (:id measure))
    (assoc :name (str "Measure " (:id measure)))))

(mr/def ::dimension-id
  "UUID string identifying a dimension."
  ::lib.schema.common/uuid)

(mr/def ::dimension-group
  "Group descriptor for a dimension, indicating which table it belongs to."
  [:map {:closed true}
   [:id :string]
   [:type [:enum "main" "connection"]]
   [:display-name :string]])

(mr/def ::dimension-source.type
  [:enum :field])

(mr/def ::dimension-source
  [:map {:closed true}
   [:type     ::dimension-source.type]
   [:field-id {:optional true} [:maybe ::lib.schema.id/field]]
   [:binning  {:optional true} [:maybe :boolean]]])

(mr/def ::dimension-mapping.type
  "Type of dimension mapping."
  [:enum :table])

(mr/def ::dimension-mapping.target
  "Target field reference for a dimension mapping, e.g. [:field {:source-field 1} 2]."
  [:ref :mbql.clause/field])

(mr/def ::dimension-mapping
  "Schema for a dimension mapping."
  [:map {:closed true}
   [:type         ::dimension-mapping.type]
   [:table-id     {:optional true} [:maybe ::lib.schema.id/table]]
   [:dimension-id ::dimension-id]
   [:target       ::dimension-mapping.target]])

(mr/def ::dimension-status
  "Status of a dimension indicating whether it's active or has issues.
   - :status/active   - Column exists, dimension is usable
   - :status/orphaned - Column was removed from schema, dimension preserved for reference"
  [:enum :status/active :status/orphaned])

(mr/def ::persisted-dimension
  "Schema for a persisted dimension definition with status tracking.
   Persisted dimensions include additional metadata about their status
   and any issues that prevent them from being used.
   Note: target field references are stored in dimension-mappings, not here."
  [:map {:closed true}
   [:id               ::dimension-id]
   [:name             {:optional true} [:maybe :string]]
   [:display-name     {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:description      {:optional true} [:maybe :string]]
   [:effective-type   {:optional true} [:maybe ::lib.schema.common/base-type]]
   [:semantic-type    {:optional true} [:maybe ::lib.schema.common/semantic-or-relation-type]]
   [:has-field-values {:optional true} [:maybe [:enum :list :search :none]]]
   [:status           {:optional true} [:maybe ::dimension-status]]
   [:status-message   {:optional true} [:maybe :string]]
   [:sources          {:optional true} [:maybe [:sequential ::dimension-source]]]
   [:group            {:optional true} [:maybe ::dimension-group]]
   [:lib/source       {:optional true} [:maybe [:or [:ref ::column.source] :string]]]
   [:default-temporal-unit {:optional true} ::lib.schema.temporal-bucketing/unit]
   ;; At most one dimension per entity may be the default.
   [:default          {:optional true} [:maybe :boolean]]])

(mr/def ::measure
  "More or less the same as a [[metabase.measures.models.measure]], but with kebab-case keys."
  [:map
   {:closed true, :error/message "Valid Measure metadata"
    :decode/mock   mock-measure}
   [:lib/type   [:= :metadata/measure]]
   [:id         ::lib.schema.id/measure]
   [:name       ::lib.schema.common/non-blank-string]
   [:table-id   ::lib.schema.id/table]
   ;; the MBQL snippet defining this Measure, contains an aggregation expression.
   ;; Strict validation via ::lib.schema.measure/definition happens in metabase.measures.models.measure
   [:definition [:maybe [:ref ::measure.definition]]]
   [:description {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:archived    {:optional true} [:maybe :boolean]]
   [:dimensions         {:optional true} [:maybe [:sequential [:ref ::persisted-dimension]]]]
   [:dimension-mappings {:optional true} [:maybe [:sequential [:ref ::dimension-mapping]]]]
   [:aggregation-positions {:optional true} [:maybe [:sequential :int]]]])

(mr/def ::metric
  "A V2 Metric! This a special subtype of a Card. Not convinced we really need this as opposed to just using `::card` --
  see my notes on `::type`."
  [:merge
   ::card
   [:map
    [:lib/type [:= :metadata/metric]]
    [:type     [:= :metric]]
    [:lib/join-alias {:optional true} ::lib.schema.common/non-blank-string]
    [:aggregation-position {:optional true} [:maybe [:int {:min 0}]]]]])

(mr/def ::native-query-snippet
  [:map {:closed true}
   [:lib/type      [:= :metadata/native-query-snippet]]
   [:id            ::lib.schema.id/native-query-snippet]
   [:name          {:optional true} [:maybe :string]]
   [:description   {:optional true} [:maybe :string]]
   [:content       {:optional true} [:maybe :string]]
   [:archived      {:optional true} [:maybe :boolean]]
   [:collection-id {:optional true} [:maybe ::lib.schema.id/collection]]
   [:template-tags {:optional true} [:maybe [:ref ::lib.schema.template-tag/template-tag-map]]]])

(mr/def ::table
  "Schema for metadata about a specific [[metabase.warehouse-schema.models.table]]. More or less the same but with
  kebab-case keys."
  [:map
   {:closed true, :error/message "Valid Table metadata"}
   [:lib/type [:= :metadata/table]]
   [:id       ::lib.schema.id/table]
   [:name     ::lib.schema.common/non-blank-string]
   [:display-name {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:schema       {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   ;; Optional `:db` AST slot for cross-DB references (BigQuery `project.dataset.table`,
   ;; SQL Server / Snowflake `db.schema.table`). Sync doesn't populate it on standard
   ;; reads — only cross-DB rewriters fill it.
   [:db           {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:db-id                   {:optional true} ::lib.schema.id/database]
   [:active                  {:optional true} [:maybe :boolean]]
   [:visibility-type         {:optional true} [:maybe [:or :keyword :string]]]
   [:database-require-filter {:optional true} [:maybe :boolean]]
   [:description             {:optional true} [:maybe :string]]
   [:entity-type             {:optional true} [:maybe [:or :keyword :string]]]
   [:caveats                 {:optional true} [:maybe :string]]
   [:points-of-interest      {:optional true} [:maybe :string]]
   [:field-order             {:optional true} [:maybe [:or :keyword :string]]]
   [:initial-sync-status     {:optional true} [:maybe [:or :string :keyword]]]
   [:show-in-getting-started {:optional true} [:maybe :boolean]]
   [:is-upload               {:optional true} [:maybe :boolean]]
   [:entity-id               {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:created-at              {:optional true} [:maybe [:or :string #?(:clj (lib.schema.common/instance-of-class java.time.temporal.Temporal))]]]
   [:updated-at              {:optional true} [:maybe [:or :string #?(:clj (lib.schema.common/instance-of-class java.time.temporal.Temporal))]]]
   [:deactivated-at          {:optional true} [:maybe [:or :string #?(:clj (lib.schema.common/instance-of-class java.time.temporal.Temporal))]]]
   [:archived-at             {:optional true} [:maybe [:or :string #?(:clj (lib.schema.common/instance-of-class java.time.temporal.Temporal))]]]
   [:estimated-row-count     {:optional true} [:maybe :int]]
   [:view-count              {:optional true} [:maybe :int]]
   [:is-writable             {:optional true} [:maybe :boolean]]
   [:data-authority          {:optional true} [:maybe [:or :keyword :string]]]
   [:data-source             {:optional true} [:maybe [:or :keyword :string]]]
   [:data-layer              {:optional true} [:maybe [:or :keyword :string]]]
   [:owner-email             {:optional true} [:maybe :string]]
   [:owner-user-id           {:optional true} [:maybe ::lib.schema.id/user]]
   [:collection-id           {:optional true} [:maybe ::lib.schema.id/collection]]
   [:is-published            {:optional true} [:maybe :boolean]]
   [:transform-id            {:optional true} [:maybe ::lib.schema.id/transform]]
   [:transform-target        {:optional true} [:maybe :boolean]]])

(mr/def ::database
  "Malli schema for the DatabaseMetadata as returned by `GET /api/database/:id/metadata` -- what should be available to
  the frontend Query Builder."
  [:map
   {:closed true, :error/message "Valid Database metadata"}
   [:lib/type [:= :metadata/database]]
   [:id ::lib.schema.id/database]
   ;; TODO -- this should validate against the driver features list in [[metabase.driver/features]] if we're in
   ;; Clj mode
   [:dbms-version    {:optional true} [:maybe [:map {:closed true}
                                               [:flavor           {:optional true} [:maybe :string]]
                                               [:version          {:optional true} [:maybe :string]]
                                               [:semantic-version {:optional true} [:maybe [:or
                                                                                            [:sequential :int]
                                                                                            [:map {:closed true} [:major :int] [:minor :int]]]]]
                                               [:cloud            {:optional true} [:maybe :boolean]]
                                               [:single-node      {:optional true} [:maybe :boolean]]]]]
   [:details         {:optional true} ::lib.schema.common/database-details]
   [:engine          {:optional true} [:keyword {:decode/normalize lib.schema.common/normalize-keyword}]]
   [:features        {:optional true} [:set [:keyword {:decode/normalize lib.schema.common/normalize-keyword}]]]
   [:is-audit        {:optional true} :boolean]
   [:is-attached-dwh {:optional true} :boolean]
   [:settings        {:optional true} [:maybe [:ref ::lib.schema.common/visualization-settings]]]
   [:name                        {:optional true} [:maybe :string]]
   [:description                 {:optional true} [:maybe :string]]
   [:timezone                    {:optional true} [:maybe :string]]
   [:default-schema              {:optional true} [:maybe :string]]
   [:write-data-details          {:optional true} [:maybe ::lib.schema.common/database-details]]
   [:admin-details               {:optional true} [:maybe ::lib.schema.common/database-details]]
   [:router-database-id          {:optional true} [:maybe ::lib.schema.id/database]]
   [:auto-run-queries            {:optional true} [:maybe :boolean]]
   [:cache-field-values-schedule {:optional true} [:maybe :string]]
   [:metadata-sync-schedule      {:optional true} [:maybe :string]]
   [:cache-ttl                   {:optional true} [:maybe :int]]
   [:caveats                     {:optional true} [:maybe :string]]
   [:points-of-interest          {:optional true} [:maybe :string]]
   [:creator-id                  {:optional true} [:maybe pos-int?]]
   [:initial-sync-status         {:optional true} [:maybe [:or :string :keyword]]]
   [:is-full-sync                {:optional true} [:maybe :boolean]]
   [:is-on-demand                {:optional true} [:maybe :boolean]]
   [:is-sample                   {:optional true} [:maybe :boolean]]
   [:native-permissions          {:optional true} [:maybe [:or :keyword :string]]]
   [:options                     {:optional true} [:maybe [:ref ::lib.schema.common/database-settings]]]
   [:refingerprint               {:optional true} [:maybe :boolean]]
   [:is-stub                     {:optional true} [:maybe :boolean]]
   [:provider-name               {:optional true} [:maybe :string]]
   [:uploads-enabled             {:optional true} [:maybe :boolean]]
   [:uploads-schema-name         {:optional true} [:maybe :string]]
   [:uploads-table-prefix        {:optional true} [:maybe :string]]
   [:can-manage                  {:optional true} [:maybe :boolean]]
   [:can-upload                  {:optional true} [:maybe :boolean]]
   [:router-user-attribute       {:optional true} [:maybe :string]]
   [:transforms-permissions      {:optional true} [:maybe [:or :keyword :string]]]
   [:created-at                  {:optional true} [:maybe [:or :string #?(:clj (lib.schema.common/instance-of-class java.time.temporal.Temporal))]]]
   [:updated-at                  {:optional true} [:maybe [:or :string #?(:clj (lib.schema.common/instance-of-class java.time.temporal.Temporal))]]]])

(mr/def ::metadata-provider
  "Schema for something that satisfies the [[metabase.lib.metadata.protocols/MetadataProvider]] protocol."
  [:ref :metabase.lib.metadata.protocols/metadata-provider])

(mr/def ::metadata-providerable
  "Something that can be used to get a MetadataProvider. Either a MetadataProvider, or a map with a MetadataProvider in
  the key `:lib/metadata` (i.e., a query)."
  [:ref :metabase.lib.metadata.protocols/metadata-providerable])

(mr/def ::stage
  "Metadata about the columns returned by a particular stage of a MBQL 5 query. For example a single-stage native query
  like

    {:database 1
     :lib/type :mbql/query
     :stages   [{:lib/type :mbql.stage/mbql
                 :native   \"SELECT id, name FROM VENUES;\"}]}

  might have stage metadata like

    {:columns [{:name \"id\", :base-type :type/Integer}
               {:name \"name\", :base-type :type/Text}]}

  associated with the query's lone stage.

  At some point in the near future we will hopefully attach this metadata directly to each stage in a query, so a
  multi-stage query will have `:lib/stage-metadata` for each stage. The main goal is to facilitate things like
  returning lists of visible or filterable columns for a given stage of a query. This is TBD, see #28717 for a WIP
  implementation of this idea.

  This is the same format as the results metadata returned with QP results in `data.results_metadata`. The `:columns`
  portion of this (`data.results_metadata.columns`) is also saved as `Card.result_metadata` for Saved Questions.

  Note that queries currently actually come back with both `data.results_metadata` AND `data.cols`; it looks like the
  Frontend actually *merges* these together -- see `applyMetadataDiff` in
  `frontend/src/metabase/query_builder/selectors.js` -- but this is ridiculous. Let's try to merge anything missing in
  `results_metadata` into `cols` going forward so things don't need to be manually merged in the future."
  [:map
   {:decode/normalize lib.schema.common/normalize-map :closed true}
   [:lib/type [:= {:default :metadata/results} :metadata/results]]
   [:columns [:sequential ::column]]])

(mr/def ::transform.source-incremental-strategy
  "Incremental strategy of a Transform source, as stored in the app DB."
  [:map {:closed true}
   [:type                       [:or :keyword :string]]
   [:checkpoint-filter-field-id {:optional true} [:maybe ::lib.schema.id/field]]
   [:lookback                   {:optional true} [:maybe [:map {:closed true}
                                                          [:value pos-int?]
                                                          [:unit  :string]]]]])

(mr/def ::transform.target-incremental-strategy
  "Incremental strategy of a Transform target, as stored in the app DB."
  [:map {:closed true}
   [:type       [:or :keyword :string]]
   [:unique-key {:optional true} [:sequential [:map {:closed true}
                                               [:name     {:optional true} :string]
                                               [:field-id {:optional true} [:maybe ::lib.schema.id/field]]]]]])

(mr/def ::transform.source-table
  "One source table entry of a Python Transform source, as stored in the app DB."
  [:map {:closed true}
   [:alias       :string]
   [:database_id {:optional true} [:maybe :int]]
   [:schema      {:optional true} [:maybe :string]]
   [:table       {:optional true} :string]
   [:table_id    {:optional true} [:maybe :int]]])

(mr/def ::transform.source
  "The `:source` of a Transform: a query or a Python script."
  [:map {:closed true}
   [:type                        {:optional true} [:or :keyword :string]]
   [:query                       {:optional true} [:ref ::card.query]]
   [:source-incremental-strategy {:optional true} [:maybe [:ref ::transform.source-incremental-strategy]]]
   [:source-database             {:optional true} [:maybe :int]]
   [:source-tables               {:optional true} [:sequential [:ref ::transform.source-table]]]
   [:body                        {:optional true} :string]])

(mr/def ::transform.target
  "The `:target` table of a Transform."
  [:map {:closed true}
   [:type                        {:optional true} [:or :keyword :string]]
   [:database                    {:optional true} [:maybe ::lib.schema.id/database]]
   [:schema                      {:optional true} [:maybe :string]]
   [:name                        {:optional true} :string]
   [:target-incremental-strategy {:optional true} [:maybe [:ref ::transform.target-incremental-strategy]]]])

(mr/def ::transform
  "TODO (Cam 10/1/25) -- I'm putting this here as a placeholder until you guys go fill it out a little more."
  [:map {:closed true}
   [:lib/type [:= :metadata/transform]]
   [:id     ::lib.schema.id/transform]
   [:name        {:optional true} :string]
   [:source      {:optional true} [:ref ::transform.source]]
   [:source-type {:optional true} [:maybe [:enum :python :native :mbql]]]
   [:target      {:optional true} [:ref ::transform.target]]])
